import { QueryClient, QueryClientProvider, useIsFetching, useIsMutating } from '@workspace/api';
import React, { ReactNode, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { GlobalFormProvider, useGlobalForm, ModalProvider } from '../contexts';
import FormLayout from '../layouts/form-layout';
import Key from '../pages/key';
import type { StepProps } from '../utils';
import type { StateResolver, FormConfig } from '../types';
import { StepForm } from './step-form';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            refetchOnReconnect: false,
            staleTime: Infinity,
            retry: false,
        },
    },
});

export interface AppFormProps {
  steps: StepProps[];
  children?: ReactNode;
  initialFormState?: any;
  origin?: string;
  stateResolver?: StateResolver; // Optionnel pour rester compatible
  config?: FormConfig; // Configuration optionnelle du formulaire
}

export function AppForm({ steps, children, initialFormState, origin, stateResolver, config }: AppFormProps) {
    return (
        <BaseApp>
            <GlobalFormProvider 
                initialFormState={initialFormState} 
                steps={steps} 
                origin={origin}
                stateResolver={stateResolver}
                config={config}
            >
                <LoaderRoot />
                <RoutesRoot />
                {children}
            </GlobalFormProvider>
        </BaseApp>
    );
}

function BaseApp({ children }: { children: ReactNode }) {
    return (
        <React.StrictMode>
            <QueryClientProvider client={queryClient}>
                <ModalProvider>
                    <BrowserRouter>{children}</BrowserRouter>
                </ModalProvider>
            </QueryClientProvider>
        </React.StrictMode>
    );
}

function LoaderRoot() {
    const isGlobalFetching = useIsFetching();
    const isGlobalMutating = useIsMutating();

    useEffect(() => {
        if (isGlobalFetching || isGlobalMutating) {
            document.body.style.cursor = 'wait';
        } else {
            document.body.style.cursor = 'default';
        }
    }, [isGlobalFetching, isGlobalMutating]);

    return null;
}

function RoutesRoot() {
    const { steps } = useGlobalForm();

    return (
        <Routes>
            <Route element={<FormLayout />}>
                {steps.map((step) => (
                    <Route element={<StepForm {...step} />} key={step.path} path={step.path} />
                ))}
            </Route>
            <Route element={<Key />} path="/:key" />
            <Route element={<Navigate to={steps[0].path} />} path="/" />
        </Routes>
    );
}
